import React, { useContext, type KeyboardEvent } from "react";

import { IntlContext } from "context";
import type { CatalogService } from "types/service";
import { Badge } from "components/ui/badge";
import { cn } from "lib/utils";
import { useProviderServiceChips, type ProviderServiceChipLevel } from "./useProviderServiceChips";

interface Props {
    catalog: CatalogService[];
    chipLevel?: ProviderServiceChipLevel;
    className?: string;
    iconClassName?: string;
    onChipClick?: () => void;
}

export const ProviderServiceChips: React.FC<Props> = ({
    catalog,
    chipLevel = "subcategory",
    className,
    iconClassName = "h-3.5 w-3.5",
    onChipClick,
}) => {
    const intl = useContext(IntlContext);
    const chips = useProviderServiceChips(catalog, chipLevel);
    const isInteractive = Boolean(onChipClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onChipClick?.();
        }
    };

    if (chips.length === 0) return null;

    return (
        <div className={className}>
            <p className="mb-2 text-base font-bold text-foreground">
                {intl.formatMessage("provider.card.services_label")}
            </p>
            <div className="flex flex-wrap gap-1.5">
                {chips.map(({ key, label, palette, Icon }) => (
                    <Badge
                        key={key}
                        variant="categorySquare"
                        palette={palette}
                        role={isInteractive ? "button" : undefined}
                        tabIndex={isInteractive ? 0 : undefined}
                        className={cn(isInteractive && "cursor-pointer transition-opacity hover:opacity-85")}
                        onClick={onChipClick}
                        onKeyDown={isInteractive ? handleKeyDown : undefined}
                    >
                        <Icon className={iconClassName} />
                        {label}
                    </Badge>
                ))}
            </div>
        </div>
    );
};
